/**
 * Service layer — submittal revision file attachments.
 * Wraps /api/submittal-files.
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/submittal-files' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'submittal-files error: ' + res.status)
  }
  return res.json()
}

/** List files attached to a specific revision. */
export function getRevisionFiles(revisionId) {
  if (!revisionId) return Promise.resolve([])
  return call('GET', null, 'revisionId=' + encodeURIComponent(revisionId)).then(d => d.files || [])
}

/**
 * Upload a file to a revision. Returns the new file record.
 * @param {string} revisionId
 * @param {string} submittalId
 * @param {File}   file        — browser File object
 * @param {string} [kind]      — 'package' (submitted package, default) | 'review' (reviewer markup)
 */
export async function uploadRevisionFile(revisionId, submittalId, file, kind = 'package') {
  // 1. Get signed upload URL
  const { signedUrl, path, publicUrl } = await call('POST', {
    action: 'getUploadUrl', revisionId, submittalId,
    filename: file.name, contentType: file.type || 'application/octet-stream',
  })
  // 2. PUT directly to Supabase Storage
  const putRes = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!putRes.ok) throw new Error('Storage upload failed: ' + putRes.status)
  // 3. Confirm and get the DB record
  return call('POST', {
    action: 'confirmUpload', revisionId, submittalId,
    filename: file.name, url: publicUrl, storagePath: path,
    fileSize: file.size, fileType: null, kind,
  }).then(d => d.file)
}

/** List submittal-level attachments (files not tied to a review revision). */
export function getSubmittalFiles(submittalId) {
  if (!submittalId) return Promise.resolve([])
  return call('GET', null, 'submittalId=' + encodeURIComponent(submittalId)).then(d => d.files || [])
}

/** Upload a file as a submittal-level attachment (revision_id null). Returns the new file record. */
export async function uploadSubmittalFile(submittalId, file) {
  const { signedUrl, path, publicUrl } = await call('POST', {
    action: 'getUploadUrl', submittalId,
    filename: file.name, contentType: file.type || 'application/octet-stream',
  })
  const putRes = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!putRes.ok) throw new Error('Storage upload failed: ' + putRes.status)
  return call('POST', {
    action: 'confirmUpload', submittalId,
    filename: file.name, url: publicUrl, storagePath: path,
    fileSize: file.size, fileType: null,
  }).then(d => d.file)
}

/** Delete a file record (also removes from Storage). */
export function deleteRevisionFile(fileId) {
  return call('POST', { action: 'deleteFile', fileId }).then(d => d.ok)
}
